import { useCallback, useEffect, useState } from 'react';
import { useIsUserSessionExpired } from '../configuration/login/loginSlice';
import { useAppSelector } from '../configuration/setup/hooks';

const MAX_TIMEOUT_MS = 2_147_483_647;
const CLOCK_SKEW_MS = 60_000;

const getTokenExpiry = (token?: string | null): number | undefined => {
    const payload = token?.split('.')[1];
    if (!payload) {
        return undefined;
    }
    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const { exp } = JSON.parse(atob(padded)) as { exp?: unknown };
        return typeof exp === 'number' ? exp * 1000 + CLOCK_SKEW_MS : undefined;
    } catch {
        return undefined;
    }
};

const isUnauthorizedError = (error: unknown) =>
    (error as { response?: { status?: number } } | undefined)?.response?.status === 401;

const useSessionValidity = () => {
    const sessionExpired = useIsUserSessionExpired();
    const token = useAppSelector(state => state.tokenHandling.accessToken);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [unauthorized, setUnauthorized] = useState(false);

    useEffect(() => {
        setUnauthorized(false);
        const expiresAt = getTokenExpiry(token);
        if (expiresAt === undefined) {
            setTokenExpired(false);
            return;
        }
        const remaining = expiresAt - Date.now();
        if (remaining <= 0) {
            setTokenExpired(true);
            return;
        }
        setTokenExpired(false);
        const timer = setTimeout(() => setTokenExpired(true), Math.min(remaining, MAX_TIMEOUT_MS));
        return () => clearTimeout(timer);
    }, [token]);

    const reportError = useCallback((error: unknown) => {
        if (isUnauthorizedError(error)) {
            setUnauthorized(true);
        }
    }, []);

    return { sessionValid: !sessionExpired && !tokenExpired && !unauthorized, reportError };
};

export default useSessionValidity;
